/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */

import {
  MapNodeType,
  TasksApi,
  ApproveTaskRequest,
  UpdateTaskRequest,
  MapData,
  ResultTaskList,
  SelectTaskList,
  RegisterTaskData,
} from "client";
import {
  useEffect,
  useLayoutEffect,
  useState,
  FC,
  useMemo,
  ReactNode,
  useCallback,
  useRef,
} from "react";
import { Dialog, Typography } from "@mui/material";
import {
  MapArea,
  DetailArea,
  DialogContainer,
  getTaskPlanNodeIds,
  getTaskDataConvert,
} from "./Util";
import { Map } from "../Map/Map";
import {
  NodeMapOverlay,
  HighlightEdgePolygon,
  HighlightNodePolygon,
  getNodeType,
} from "components/Map/NodeMapOverlay";
import { ResetMapHeading } from "components/Map/ResetMapHeading";
import { ResetMapPosition } from "components/Map/ResetMapPosition";
import { useUserContext } from "context/UserContext";
import { SelectTaskPanel } from "./SelectTaskPanel";
import {
  PassPointInfo,
  PointSource,
  SelectPointPanel,
} from "./SelectPointPanel";
import { SettingPanel } from "./SettingPanel";
import { ConfirmPanel } from "./ConfirmPanel";
import { ConnectingDialog } from "components/Common/ConnectingDialog";
import { ErrorDialog } from "components/Common/ErrorDialog";
import {
  ConfirmDialog,
  ConfirmDialogProps,
} from "components/Common/ConfirmDialog";
import { TaskData, useTaskDataContext } from "context/TaskDataProvider";
import { useMapDataContext } from "context/MapDataProvider";
import { useTranslation } from "react-i18next";
import { Const, INTERVAL_COUNT } from "const";
import {
  TaskControlState,
  EXITS_NODES_TASK_REGISTER_PROCESSES,
  NEW_NODES_TASK_REGISTER_PROCESSES,
  NO_TASK_LIST_PROCESSES,
  PHASE_SELECT_POINT,
  PHASE_SELECT_TASK,
  PHASE_TASK_SETTING,
  PHASE_TASK_CONFIRM,
  APPROVE_ONLY_PROCESSES,
} from "./consts";
import { ButtonContext, TaskControlPanel } from "./TaskControlPanel";
import { AutoStartNodeMapOverlay } from "components/Map/AutoStartNodeMapOverlay";
import {
  getErrCodeMsg,
  getErrorResponse,
  getExtendErrMsgCode,
} from "utils/Api";
import { useApiWithTimeout } from "hooks/ApiWrapperWithTimeout";
import {
  SystemErrorDialog,
  SystemErrorDialogProps,
} from "components/Common/SystemErrorDialog";
import { useGetTaskList } from "hooks/GetTaskListHook";
import { SpinnerDelay } from "components/Common/SpinnerDelay";
import { getElapsedTime } from "utils/DateTime";
import { TimeoutError } from "exception/TimeoutError";
import { useGetTask } from "hooks/GetTaskHook";
import { useWaitTaskStatus } from "hooks/WaitTaskStatusHook";

const defaultTaskControlState: TaskControlState = {
  process: [],
  processTitles: [],
  phase: 0,
};

const initializeTaskData: TaskData = {};

export type TaskControlMode = "register" | "edit" | "approve" | "goHome";

/**
 * Type of control target
 */
export interface TargetTask {
  /** Task */
  task: ResultTaskList;
  /** Control mode */
  mode: TaskControlMode;
}

export type TaskControlStatus =
  | "completed"
  | "incomplete"
  | "failed"
  | "canceled";
export interface TaskControlResult {
  status: TaskControlStatus;
  task?: TaskData;
}

interface TaskControlDialogProps {
  isOpen: boolean;
  target: TargetTask;
  onClose: (result: TaskControlResult) => void;
}

export const TaskControlDialog: FC<TaskControlDialogProps> = ({
  isOpen,
  target,
  onClose,
}) => {
  const { t } = useTranslation();
  const { taskData, setTaskData } = useTaskDataContext();
  const taskDataRef = useRef(taskData);
  const { mapData } = useMapDataContext();
  const user = useUserContext();

  const mapDataRef = useRef(mapData);
  const defaultTaskName = useRef("");

  const [currentPanel, setCurrentPanel] = useState<SidePanelContext>();
  const [isTilesLoaded, setIsTilesLoaded] = useState(false);
  const mapProps = useMemo(() => {
    return {
      mapContainerStyle: {
        height: "100%",
        width: "100%",
      },
      options: {
        disableDefaultUI: true,
        mapTypeControl: true,
        zoomControl: true,
        gestureHandling: "greedy",
        keyboardShortcuts: false,
        mapId: process.env.REACT_APP_GOOGLEMAP_MAPID,
      },
      center: {
        lat: user.jobsite?.latitude ?? 0,
        lng: user.jobsite?.longitude ?? 0,
      },
      zoom: user.jobsite?.zoom ?? 11,
      onTilesLoaded: () => {
        setIsTilesLoaded(true);
      },
    };
  }, [user.jobsite]);

  const [isConnecting, setIsConnecting] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  // Contents of confirm dialog
  const [confirmContents, setConfirmContents] = useState<ConfirmDialogProps>();

  const panelTitleMap: { [key: string]: string } = useMemo(() => {
    return {
      [PHASE_SELECT_TASK]: t("AWVUI-MSG115"),
      [PHASE_SELECT_POINT]: t("AWVUI-MSG116"),
      [PHASE_TASK_SETTING]: t("AWVUI-MSG117"),
      [PHASE_TASK_CONFIRM]: t("AWVUI-MSG118"),
    };
  }, [t]);
  const [taskControlState, setTaskControlState] = useState<TaskControlState>(
    defaultTaskControlState
  );

  const [selectableTaskList, setSelectableTaskList] = useState<
    SelectTaskList[]
  >([]);

  const [selectedNode, setSelectedNode] = useState<MapNodeType>();
  const [highlightNodeIds, setHighlightNodeIds] = useState<Array<number>>([]);
  const [autoStartNodes, setAutoStartNodes] = useState<MapNodeType[]>([]);

  const [preUpdateTaskData, setPreUpdateTaskData] =
    useState<TaskData>(initializeTaskData);

  // interval state
  const [subscribeTimeOutId, setSubscribeTimeOutId] = useState(0);
  const [subscriptionContext, setSubscriptionContext] =
    useState<SubscriptionContext>();

  // Flag if can move forward or not
  const [canForward, setCanForward] = useState(false);
  // List of point node
  const [pointSourceList, setPointSourceList] = useState<PointSource[]>([]);

  const [getTasksApi, cancelGetTasksApi] = useApiWithTimeout(
    TasksApi,
    Const.operationTimeout.getTask
  );
  const [registerTasksApi, cancelRegisterTasksApi] = useApiWithTimeout(
    TasksApi,
    Const.operationTimeout.registerTask
  );
  const [updateTasksApi, cancelUpdateTasksApi] = useApiWithTimeout(
    TasksApi,
    Const.operationTimeout.updateTask
  );
  const [deleteTasksApi, cancelDeleteTasksApi] = useApiWithTimeout(
    TasksApi,
    Const.operationTimeout.deleteTask
  );
  const [taskCreationStatusApi, cancelTaskCreationStatusApi] =
    useApiWithTimeout(TasksApi, Const.operationTimeout.getTaskCreationProgress);
  const [tasksApi, cancelTasksApi] = useApiWithTimeout(
    TasksApi,
    Const.operationTimeout.approveTask
  );

  const getTaskList = useGetTaskList();
  const getTask = useGetTask();
  const getTaskExecute = getTask.setRequest;
  const getTaskListExecute = getTaskList.setRequest;
  const waitTaskStatus = useWaitTaskStatus();

  const [showApiError, setShowApiError] = useState<SystemErrorDialogProps>();
  const errorStatus = useRef<TaskControlStatus>("failed");

  const cancelAllApi = useCallback(() => {
    cancelGetTasksApi();
    cancelUpdateTasksApi();
    cancelDeleteTasksApi();
    cancelRegisterTasksApi();
    cancelTaskCreationStatusApi();
    cancelTasksApi();
  }, [
    cancelGetTasksApi,
    cancelUpdateTasksApi,
    cancelDeleteTasksApi,
    cancelRegisterTasksApi,
    cancelTaskCreationStatusApi,
    cancelTasksApi,
  ]);

  // **************************************
  // Callbacks
  // **************************************

  /**
   * Action for task selected.
   * @param{string} taskId Selected task ID
   */
  const handleTaskSelected = useCallback(
    (taskId: string) => {
      const selectedTask = selectableTaskList.find(
        (task) => task.jobSetId === taskId
      );
      if (selectedTask !== undefined) {
        // Get item values from selected task
        setTaskData((current) => {
          const updatedTask: TaskData = {
            ...current,
            jobSetName: selectedTask.jobSetName,
            startNodeId: selectedTask.startNodeId,
            endNodeId: selectedTask.endNodeId,
            jobInfos: selectedTask.jobInfos,
            memo: selectedTask.memo,
            jobSetId: selectedTask.jobSetId,
            startDatetime: undefined,
            updateDate: new Date(),
            extensionTimeLoad: selectedTask.extensionTimeLoad,
            srcJobSetId: taskId,
          };
          // Highlight route
          setHighlightNodeIds(getTaskPlanNodeIds(updatedTask));

          return updatedTask;
        });

        setCanForward(true);
      } else {
        setHighlightNodeIds([]);
        setCanForward(false);
      }
    },
    [selectableTaskList, setTaskData]
  );

  /**
   * Action for pass points list changed
   * @param{PassPointInfo[]} passPoints List of pass points
   */
  const handlePassPointsChanged = useCallback(
    (passPoints: PassPointInfo[]) => {
      // highlight node
      const nodeIds = passPoints
        .map((node) => node.nodeId)
        .filter(
          (elm): elm is Exclude<typeof elm, undefined> => elm !== undefined
        );
      setHighlightNodeIds(nodeIds);

      // Show auto start point
      const allNodes = mapDataRef.current.mapInfo.flatMap((m) => m.nodes);
      setAutoStartNodes(
        // exclude end node
        passPoints
          .slice(0, -1)
          .filter((p) => p.nodeId !== undefined && p.startType === "auto")
          .map((p) => allNodes.find((n) => n?.id === p.nodeId)) as MapNodeType[]
      );

      // Update default task name
      const warehouseLabel = t("AWVUI-MSG110");
      const pointNames = passPoints.map((p) =>
        pointSourceList
          .find((s) => s.nodeId === p.nodeId)
          ?.label?.replace(`${warehouseLabel} `, "")
      );
      // maxLength 50
      defaultTaskName.current = `${warehouseLabel} ${pointNames.join(
        " to "
      )}`.substring(0, 50);

      // Update can forward to setting panel
      setCanForward(
        passPoints.length > 1 && passPoints.every((p) => p.nodeId !== undefined)
      );
    },
    [pointSourceList, t]
  );

  /**
   * Subscribing task creation status
   */
  const subscribeTaskCreationStatus = useCallback(async () => {
    if (subscriptionContext) {
      try {
        const result = await taskCreationStatusApi.getTaskCreateProgress({
          jobSetId: subscriptionContext.jobsetId,
        });
        console.debug(`result.jobSetStatus: ${result.jobSetStatus}`);
        switch (result.jobSetStatus) {
          case 1:
          case 2:
            // processing
            setSubscribeTimeOutId(0);
            break;
          case 6:
            // succeed
            subscriptionContext.onSucceed?.(subscriptionContext.jobsetId);
            // Finish subscribing
            setSubscriptionContext(undefined);
            break;
          default:
            // failed
            throw new Error();
        }
        if (
          getElapsedTime(subscriptionContext.timeout.startTimestamp) >
          subscriptionContext.timeout.timeout
        ) {
          // Timed out
          throw new TimeoutError("");
        }
      } catch (error) {
        // failed
        subscriptionContext.onError?.(error);
        // Finish subscribing
        setSubscriptionContext(undefined);
      }
    }
  }, [subscriptionContext, taskCreationStatusApi]);

  /**
   * Get task
   * @param {string} jobSetId jobsetId
   * @return {Promise<TaskData|undefined>}
   */
  const queryTaskData = useCallback(
    async (jobSetId: string): Promise<TaskData | undefined> => {
      const result = await getTasksApi.getTask({
        frameTypeAndNumberList: [taskDataRef.current.frameTypeAndNumber!],
        mapId: mapDataRef.current.mapId,
        jobSetId: jobSetId,
      });
      // update task data
      if (result?.taskInfos && result.taskInfos.length > 0) {
        return getTaskDataConvert(result.taskInfos[0]);
      }
    },
    [getTasksApi]
  );

  /**
   * Delete temporary task
   */
  const deleteTask = useCallback(async () => {
    // deny task
    await tasksApi.approveTask({
      bodyJobsetApproval: [
        {
          jobSetId: taskDataRef.current.jobSetId!,
          jobSetApproval: false,
        },
      ],
    });

    // wait task status changed
    await waitTaskStatus(
      taskDataRef.current.jobSetId!,
      [5],
      Const.operationTimeout.changeApproved
    );

    // delete task
    await deleteTasksApi.deleteTask({
      taskKind: taskDataRef.current.taskKind!,
      jobSetId: taskDataRef.current.jobSetId,
      taskId: taskDataRef.current.taskId,
      frameTypeAndNumber: taskDataRef.current.frameTypeAndNumber,
    });
  }, [deleteTasksApi, tasksApi, waitTaskStatus]);

  /**
   * go next task control phase
   */
  const goNext = useCallback(() => {
    setTaskControlState((current) => {
      if (current.phase === current.process.length - 1) {
        // If current phase is a last phase, Close this dialog
        return current;
      } else {
        // Otherwise, Update phase to next
        let process = current.process;
        if (
          current.phase === 0 &&
          current.process === NEW_NODES_TASK_REGISTER_PROCESSES
        ) {
          process = EXITS_NODES_TASK_REGISTER_PROCESSES;
        }
        const newState: TaskControlState = {
          process: process,
          processTitles: process.map((p) => panelTitleMap[p]),
          phase: current.phase + 1,
        };
        return newState;
      }
    });
  }, [panelTitleMap]);

  const finish = useCallback(
    (status: TaskControlStatus) => {
      onClose({ status: status, task: taskDataRef.current });
    },
    [onClose]
  );

  /**
   * go back task control phase
   */
  const goBack = useCallback(() => {
    setTaskControlState((current) => {
      if (current.phase === 0) {
        // Show confirm dialog to canceling, if first phase.
        setConfirmContents({
          open: true,
          message:
            target.mode === "register" ? t("AWVUI-MSG10") : t("AWVUI-MSG105"),
          negativeButtonText: t("AWVUI-MSG12"),
          positiveButtonText: t("AWVUI-MSG11"),
          onNegativeButtonClick: () => {
            setConfirmContents(undefined);
          },
          onPositiveButtonClick: () => {
            setConfirmContents(undefined);
            finish("canceled");
          },
        });
        return current;
      } else {
        // Update phase to previous
        const newState: TaskControlState = {
          ...current,
          phase: current.phase - 1,
        };
        return newState;
      }
    });
  }, [finish, t, target?.mode]);

  /**
   * interrupt task control phase
   */
  const onInterrupt = useCallback(() => {
    // Show confirm dialog to canceling, if first phase.
    setConfirmContents({
      open: true,
      message:
        target.mode === "register" ? t("AWVUI-MSG10") : t("AWVUI-MSG105"),
      negativeButtonText: t("AWVUI-MSG12"),
      positiveButtonText: t("AWVUI-MSG11"),
      onNegativeButtonClick: () => {
        setConfirmContents(undefined);
      },
      onPositiveButtonClick: () => {
        setConfirmContents(undefined);
        finish("canceled");
      },
    });
  }, [finish, t, target?.mode]);

  /**
   * go to Selecting Point Phase
   */
  const goNew = useCallback(() => {
    setTaskControlState((current) => {
      const newState: TaskControlState = {
        process: NEW_NODES_TASK_REGISTER_PROCESSES,
        processTitles: NEW_NODES_TASK_REGISTER_PROCESSES.map(
          (p) => panelTitleMap[p]
        ),
        phase: current.phase + 1,
      };
      return newState;
    });
  }, [panelTitleMap]);

  /**
   * Action for after task posted
   * @param{string} jobsetId Jobset ID
   */
  const onTaskPosted = useCallback(
    async (jobsetId: string) => {
      // Get posted task
      console.debug(`jobsetId: ${jobsetId}`);
      try {
        const newTask = await queryTaskData(jobsetId);
        if (newTask) {
          setTaskData((current) => {
            newTask.srcJobSetId = current.srcJobSetId;
            return newTask;
          });
        } else {
          // task data error.
          console.error("task is not exist");
          throw new Error("task is not exist.");
        }

        // To forward process
        goNext();
      } catch (error) {
        setErrMsg(
          t(getErrMsgCode(error, target.mode)) +
            "\n\n" +
            (await getErrCodeMsg(error)) +
            t(await getExtendErrMsgCode(error))
        );
        setIsError(true);
      } finally {
        setIsConnecting(false);
      }
    },
    [goNext, queryTaskData, setTaskData, t, target?.mode]
  );

  /**
   * Action for after task post failed
   * @param {unknown} error Error object
   */
  const onTaskPostFailed = useCallback(
    async (error: unknown) => {
      // Finish connecting screen
      setIsConnecting(false);

      // Show error dialog
      setErrMsg(
        t(getErrMsgCode(error, target.mode)) +
          "\n\n" +
          (await getErrCodeMsg(error)) +
          t(await getExtendErrMsgCode(error))
      );
      setIsError(true);
    },
    [t, target?.mode]
  );

  /**
   * Revert task to before editing
   */
  const revertTask = useCallback(async () => {
    // deny task
    await tasksApi.approveTask({
      bodyJobsetApproval: [
        {
          jobSetId: preUpdateTaskData.jobSetId!,
          jobSetApproval: false,
        },
      ],
    });
    // wait task status changed
    await waitTaskStatus(
      taskDataRef.current.jobSetId!,
      [5],
      Const.operationTimeout.changeApproved
    );
    // get latest versionId
    const currentTask = await queryTaskData(preUpdateTaskData.jobSetId!);
    // task update
    const bodyUpdate: UpdateTaskRequest = {
      updateTaskData: {
        jobSetId: preUpdateTaskData.jobSetId!,
        jobSetName: preUpdateTaskData.jobSetName!,
        mapId: mapDataRef.current.mapId,
        mapVer: mapDataRef.current.mapVer,
        fromNodeId: preUpdateTaskData.startNodeId!,
        toNodeId: preUpdateTaskData.endNodeId!,
        extensionTimeLoad: preUpdateTaskData.extensionTimeLoad,
        startDateTime: preUpdateTaskData.startDatetime!,
        memo: preUpdateTaskData.memo ?? "",
        frameTypeAndNumber: preUpdateTaskData.frameTypeAndNumber!,
        jobInfos: preUpdateTaskData.jobInfos,
        versionId: currentTask?.versionId!,
      },
    };
    await updateTasksApi.updateTask(bodyUpdate);

    // Start subscribing for task creation status
    setSubscriptionContext({
      jobsetId: preUpdateTaskData.jobSetId!,
      timeout: {
        startTimestamp: new Date(),
        timeout: Const.operationTimeout.updateTask,
      },
      onSucceed: async (jobsetId) => {
        // task approval
        const bodyApproval: ApproveTaskRequest = {
          bodyJobsetApproval: [
            {
              jobSetId: jobsetId,
              jobSetApproval: true,
            },
          ],
        };
        try {
          await tasksApi.approveTask(bodyApproval);
          // wait task status changed
          await waitTaskStatus(
            jobsetId,
            [3],
            Const.operationTimeout.changeApproved
          );
          setIsConnecting(false);
          goBack();
        } catch (error) {
          onTaskPostFailed(error);
        } finally {
          setSubscriptionContext(undefined);
        }
      },
      onError: (error) => {
        setSubscriptionContext(undefined);
        onTaskPostFailed(error);
      },
    });
  }, [
    preUpdateTaskData,
    tasksApi,
    updateTasksApi,
    queryTaskData,
    goBack,
    onTaskPostFailed,
    waitTaskStatus,
  ]);

  /**
   * Revert task to before editing and interrupt the task controle
   */
  const revertTaskAndDontBack = useCallback(async () => {
    // deny task
    await tasksApi.approveTask({
      bodyJobsetApproval: [
        {
          jobSetId: preUpdateTaskData.jobSetId!,
          jobSetApproval: false,
        },
      ],
    });
    // wait task status changed
    await waitTaskStatus(
      taskDataRef.current.jobSetId!,
      [5],
      Const.operationTimeout.changeApproved
    );
    // get latest versionId
    const currentTask = await queryTaskData(preUpdateTaskData.jobSetId!);
    // task update
    const bodyUpdate: UpdateTaskRequest = {
      updateTaskData: {
        jobSetId: preUpdateTaskData.jobSetId!,
        jobSetName: preUpdateTaskData.jobSetName!,
        mapId: mapDataRef.current.mapId,
        mapVer: mapDataRef.current.mapVer,
        fromNodeId: preUpdateTaskData.startNodeId!,
        toNodeId: preUpdateTaskData.endNodeId!,
        extensionTimeLoad: preUpdateTaskData.extensionTimeLoad,
        startDateTime: preUpdateTaskData.startDatetime!,
        memo: preUpdateTaskData.memo ?? "",
        frameTypeAndNumber: preUpdateTaskData.frameTypeAndNumber!,
        jobInfos: preUpdateTaskData.jobInfos,
        versionId: currentTask?.versionId!,
      },
    };
    await updateTasksApi.updateTask(bodyUpdate);

    // Start subscribing for task creation status
    setSubscriptionContext({
      jobsetId: preUpdateTaskData.jobSetId!,
      timeout: {
        startTimestamp: new Date(),
        timeout: Const.operationTimeout.updateTask,
      },
      onSucceed: async (jobsetId) => {
        // task approval
        const bodyApproval: ApproveTaskRequest = {
          bodyJobsetApproval: [
            {
              jobSetId: jobsetId,
              jobSetApproval: true,
            },
          ],
        };
        try {
          await tasksApi.approveTask(bodyApproval);
          // wait task status changed
          await waitTaskStatus(
            jobsetId,
            [3],
            Const.operationTimeout.changeApproved
          );
          setIsConnecting(false);
          finish("canceled");
        } catch (error) {
          onTaskPostFailed(error);
        } finally {
          setSubscriptionContext(undefined);
        }
      },
      onError: (error) => {
        setSubscriptionContext(undefined);
        onTaskPostFailed(error);
      },
    });
  }, [
    tasksApi,
    preUpdateTaskData.jobSetId,
    preUpdateTaskData.jobSetName,
    preUpdateTaskData.startNodeId,
    preUpdateTaskData.endNodeId,
    preUpdateTaskData.extensionTimeLoad,
    preUpdateTaskData.startDatetime,
    preUpdateTaskData.memo,
    preUpdateTaskData.frameTypeAndNumber,
    preUpdateTaskData.jobInfos,
    waitTaskStatus,
    queryTaskData,
    updateTasksApi,
    finish,
    onTaskPostFailed,
  ]);

  /**
   * processing Setting ⇒ Confirm(register)
   */
  const processConfirmRegister = useCallback(async () => {
    // display "Connecting"
    setIsConnecting(true);
    // task approve(true)
    try {
      console.debug("taskData: %o", taskDataRef.current);
      const registerData: RegisterTaskData = {
        frameTypeAndNumber: taskDataRef.current.frameTypeAndNumber!,
        mapId: mapDataRef.current.mapId,
        mapVer: mapDataRef.current.mapVer,
        jobSetName: taskDataRef.current.jobSetName!,
        fromNodeId: taskDataRef.current.startNodeId!,
        toNodeId: taskDataRef.current.endNodeId!,
        extensionTimeLoad: taskDataRef.current.extensionTimeLoad,
        jobInfos: taskDataRef.current.jobInfos,
        startDateTime: taskDataRef.current.startDatetime!,
        memo: taskDataRef.current.memo ?? "",
        moveToStart: taskDataRef.current.moveToStart ?? true,
      };

      const result = await registerTasksApi.createTask({
        registerTaskData: registerData,
      });
      // succeed
      console.info("Created Task");
      // Start subscribing for task creation status.
      setSubscriptionContext({
        jobsetId: result.jobSetId,
        timeout: {
          startTimestamp: new Date(),
          timeout: Const.operationTimeout.registerTask,
        },
        onSucceed: onTaskPosted,
        onError: onTaskPostFailed,
      });
    } catch (error: unknown) {
      // failed
      onTaskPostFailed(error);
    }
  }, [onTaskPostFailed, onTaskPosted, registerTasksApi]);

  /**
   * processing Setting ⇒ Confirm(register)
   */
  const processConfirmUpdate = useCallback(async () => {
    // display "Connecting"
    setIsConnecting(true);
    // task update(true)
    try {
      console.debug("taskData: %o", taskDataRef.current);
      const body: UpdateTaskRequest = {
        updateTaskData: {
          jobSetId: taskDataRef.current.jobSetId!,
          jobSetName: taskDataRef.current.jobSetName!,
          mapId: mapDataRef.current.mapId,
          mapVer: mapDataRef.current.mapVer,
          fromNodeId: taskDataRef.current.startNodeId!,
          toNodeId: taskDataRef.current.endNodeId!,
          extensionTimeLoad: taskDataRef.current.extensionTimeLoad,
          startDateTime: taskDataRef.current.startDatetime!,
          memo: taskDataRef.current.memo ?? "",
          frameTypeAndNumber: taskDataRef.current.frameTypeAndNumber!,
          jobInfos: taskDataRef.current.jobInfos,
          versionId: taskDataRef.current.versionId!,
        },
      };
      await updateTasksApi.updateTask(body);
      // succeed
      console.info("Updated Task");
      // Start subscribing for task creation status.
      setSubscriptionContext({
        jobsetId: taskDataRef.current.jobSetId!,
        timeout: {
          startTimestamp: new Date(),
          timeout: Const.operationTimeout.updateTask,
        },
        onSucceed: onTaskPosted,
        onError: onTaskPostFailed,
      });
    } catch (error: unknown) {
      console.error(error);
      const errorResponse = await getErrorResponse(error);
      console.debug("errorResponse: %o", errorResponse);
      setIsConnecting(false);
      if (
        errorResponse?.errors?.includes(Const.errorCodes.taskUpdateConflict)
      ) {
        // Show confirm dialog if task update conflicts
        setConfirmContents({
          open: true,
          message: (
            <>
              <Typography variant="subtitle1">{t("AWVUI-MSG155")}</Typography>
              <Typography variant="subtitle1">{t("AWVUI-MSG156")}</Typography>
            </>
          ),
          positiveButtonText: t("AWVUI-MSG11"),
          onPositiveButtonClick: async () => {
            setConfirmContents(undefined);
            // Get latest task
            getTaskExecute({
              frameTypeAndNumberList: [taskDataRef.current.frameTypeAndNumber!],
              mapId: mapDataRef.current.mapId,
              jobSetId: taskDataRef.current.jobSetId,
            });
          },
        });
      } else {
        // failed
        errorStatus.current = "incomplete";
        setErrMsg(
          t(getErrMsgCode(error, target.mode)) +
            "\n\n" +
            (await getErrCodeMsg(error)) +
            t(await getExtendErrMsgCode(error))
        );
        setIsError(true);
      }
    }
  }, [
    onTaskPosted,
    onTaskPostFailed,
    t,
    target?.mode,
    updateTasksApi,
    getTaskExecute,
  ]);

  // Effect for getTask succeed
  useEffect(() => {
    if (getTask.value) {
      if (getTask.value.taskInfos && getTask.value.taskInfos.length > 0) {
        // Get task data and close dialog
        onClose({
          status: "incomplete",
          task: getTaskDataConvert(getTask.value.taskInfos[0]),
        });
      } else {
        // Show error message if task is not found
        setErrMsg(t("AWVUI-MSG102"));
        setIsError(true);
      }
    }
  }, [getTask.value, onClose, t]);

  // Effect for getTask failed
  useEffect(() => {
    if (!getTask.loading && getTask.error) {
      // Show error message
      setErrMsg(t(getErrMsgCode(getTask.error, target.mode)));
      setIsError(true);
    }
  }, [getTask.loading, getTask.error, t, target?.mode]);

  /**
   * processing Setting ⇒ Confirm
   */
  const processingConfirm = useCallback(() => {
    if (target.mode === "edit") {
      processConfirmUpdate();
    } else {
      processConfirmRegister();
    }
  }, [processConfirmRegister, processConfirmUpdate, target?.mode]);

  /**
   *  processing Confirm ⇒ Setting
   */
  const processingSettingBack = useCallback(async () => {
    setIsConnecting(true);
    try {
      if (target.mode === "edit") {
        // Revert task
        await revertTask();
      } else {
        // Delete task
        await deleteTask();
        setSubscriptionContext(undefined);
        setIsConnecting(false);
        goBack();
      }
    } catch (error) {
      // failed
      setErrMsg(
        t(getErrMsgCode(error, target.mode)) +
          "\n\n" +
          (await getErrCodeMsg(error)) +
          t(await getExtendErrMsgCode(error))
      );
      setIsError(true);
      setIsConnecting(false);
    }
  }, [deleteTask, goBack, revertTask, t, target?.mode]);

  /**
   *  processing Confirm ⇒ Interrupt task control phase
   */
  const processingSettingInterrupt = useCallback(async () => {
    // Show confirm dialog to canceling.
    setConfirmContents({
      open: true,
      message:
        target.mode === "register" ? t("AWVUI-MSG10") : t("AWVUI-MSG105"),
      negativeButtonText: t("AWVUI-MSG12"),
      positiveButtonText: t("AWVUI-MSG11"),
      onNegativeButtonClick: () => {
        setConfirmContents(undefined);
      },
      onPositiveButtonClick: async () => {
        setConfirmContents(undefined);
        setIsConnecting(true);
        try {
          if (target.mode === "edit") {
            // Revert task
            await revertTaskAndDontBack();
          } else {
            // Delete task
            await deleteTask();
            setSubscriptionContext(undefined);
            setIsConnecting(false);
            finish("canceled");
          }
        } catch (error) {
          // failed
          setErrMsg(
            t(getErrMsgCode(error, target.mode)) +
              "\n\n" +
              (await getErrCodeMsg(error)) +
              t(await getExtendErrMsgCode(error))
          );
          setIsError(true);
          setIsConnecting(false);
        }
      },
    });
  }, [deleteTask, finish, revertTaskAndDontBack, t, target.mode]);

  /**
   *  processing Confirm ⇒ Tasklist
   */
  const processingGoHomeCancel = useCallback(() => {
    // Show confirm dialog to canceling, if first phase.
    setConfirmContents({
      open: true,
      message: t("AWVUI-MSG10"),
      negativeButtonText: t("AWVUI-MSG12"),
      positiveButtonText: t("AWVUI-MSG11"),
      onNegativeButtonClick: () => {
        setConfirmContents(undefined);
      },
      onPositiveButtonClick: async () => {
        setConfirmContents(undefined);
        setIsConnecting(true);
        try {
          // Delete task
          await deleteTask();
          setSubscriptionContext(undefined);
          finish("canceled");
        } catch (error) {
          // failed
          setErrMsg(
            t(getErrMsgCode(error, target.mode)) +
              "\n\n" +
              (await getErrCodeMsg(error)) +
              t(await getExtendErrMsgCode(error))
          );
          setIsError(true);
        } finally {
          setIsConnecting(false);
        }
      },
    });
  }, [deleteTask, finish, t, target?.mode]);

  /**
   * submit or update Task
   */
  const submit = useCallback(async () => {
    // display "Connecting"
    setIsConnecting(true);
    // task approve(true)
    try {
      console.debug("taskData: %o", taskDataRef.current);
      const body: ApproveTaskRequest = {
        bodyJobsetApproval: [
          {
            jobSetId: taskDataRef.current.jobSetId!,
            jobSetApproval: true,
          },
        ],
      };
      await tasksApi.approveTask(body);
      // wait task status changed
      await waitTaskStatus(
        taskDataRef.current.jobSetId!,
        [3],
        Const.operationTimeout.changeApproved
      );

      // succeed
      console.info("Approved");
      setIsConnecting(false);
      finish("completed");
    } catch (error: unknown) {
      console.error(error);
      setIsConnecting(false);
      setErrMsg(
        t(getErrMsgCode(error, target.mode)) +
          "\n\n" +
          (await getErrCodeMsg(error)) +
          t(await getExtendErrMsgCode(error))
      );
      setIsError(true);
    }
  }, [finish, t, target?.mode, tasksApi, waitTaskStatus]);

  /**
   * Action for submit button
   */
  const onSubmit = useCallback(() => {
    if (autoStartNodes.length > 0) {
      // If auto start point exists, show confirm
      setConfirmContents({
        open: true,
        message: t("AWVUI-MSG158"),
        negativeButtonText: t("AWVUI-MSG12"),
        onNegativeButtonClick: () => {
          setConfirmContents(undefined);
          // Do nothing on cancel
        },
        positiveButtonText: t("AWVUI-MSG11"),
        onPositiveButtonClick: () => {
          setConfirmContents(undefined);
          // Do submit on OK
          submit();
        },
      });
    } else {
      // Do submit if auto start point not exists,
      submit();
    }
  }, [autoStartNodes.length, submit, t]);

  /**
   * get current phase.
   * @param {TaskControlState} controlState
   * @return {string} current phase
   */
  const getCurrentPhase = (controlState: TaskControlState) => {
    return controlState.process[controlState.phase];
  };

  // taskData save when taskData changed.
  useLayoutEffect(() => {
    taskDataRef.current = taskData;
  }, [taskData]);

  // mapData save when Dialog is opening only.
  useEffect(() => {
    /**
     * Resets all states
     */
    const resetStates = () => {
      setCurrentPanel(undefined);
      setIsTilesLoaded(false);
      setIsConnecting(false);
      setIsError(false);
      setErrMsg("");
      setConfirmContents(undefined);
      setTaskControlState(defaultTaskControlState);
      setSelectableTaskList([]);
      setSelectedNode(undefined);
      setHighlightNodeIds([]);
      setAutoStartNodes([]);
      setPreUpdateTaskData(initializeTaskData);
      setSubscribeTimeOutId(0);
      setSubscriptionContext(undefined);
      setCanForward(false);
      setPointSourceList([]);
      errorStatus.current = "failed";
    };
    console.debug("reset");
    resetStates();
    if (isOpen) {
      mapDataRef.current = mapData;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Side effect for dialog opened
  useEffect(() => {
    if (isOpen) {
      console.debug("target.task: %o", target.task);

      const targetTask = getTaskDataConvert(target.task);
      setTaskData(targetTask);
      if (target.mode === "register") {
        // In case register mode, Initialize by default value
        // Get existing tasks
        getTaskListExecute({
          frameTypeAndNumber: targetTask.frameTypeAndNumber!,
          mapId: mapDataRef.current.mapId,
        });
      } else {
        // In case edit or approve mode, Initialize by given task
        const processes =
          target.mode === "edit"
            ? NO_TASK_LIST_PROCESSES
            : APPROVE_ONLY_PROCESSES;
        setTaskControlState({
          process: processes,
          processTitles: processes.map((p) => panelTitleMap[p]),
          phase: 0,
        });
        setPreUpdateTaskData({ ...targetTask });
      }

      // Create point node list
      setPointSourceList(getPointSources(mapDataRef.current));
    }

    if (!isOpen) {
      cancelAllApi();
    }
    return () => {
      cancelAllApi();
    };
  }, [
    isOpen,
    target,
    setTaskData,
    cancelAllApi,
    panelTitleMap,
    getTaskListExecute,
  ]);

  // Effect for getTaskList succeed
  useEffect(() => {
    if (getTaskList.value) {
      console.debug(
        "result.selectTaskInfos: %o",
        getTaskList.value.selectTaskInfos
      );
      const existingTask: SelectTaskList[] =
        getTaskList.value.selectTaskInfos ?? [];
      console.debug(existingTask);

      if (existingTask.length === 0) {
        // If there isn't existing task, starts with selecting point
        setTaskControlState({
          process: NO_TASK_LIST_PROCESSES,
          processTitles: NO_TASK_LIST_PROCESSES.map((p) => panelTitleMap[p]),
          phase: 0,
        });
      } else {
        // If there are any existing task, starts with selecting task
        setTaskControlState({
          process: EXITS_NODES_TASK_REGISTER_PROCESSES,
          processTitles: EXITS_NODES_TASK_REGISTER_PROCESSES.map(
            (p) => panelTitleMap[p]
          ),
          phase: 0,
        });
      }
      setSelectableTaskList(existingTask);
    }
  }, [getTaskList.value, panelTitleMap]);

  // Effect for getTaskList failed
  useEffect(() => {
    if (!getTaskList.loading && getTaskList.error) {
      // failed
      setShowApiError({
        open: true,
        error: getTaskList.error,
        onClose: () => setShowApiError(undefined),
      });
      // If there isn't existing task, starts with selecting point
      setTaskControlState({
        process: NO_TASK_LIST_PROCESSES,
        processTitles: NO_TASK_LIST_PROCESSES.map((p) => panelTitleMap[p]),
        phase: 0,
      });
    }
  }, [getTaskList.error, getTaskList.loading, panelTitleMap]);

  const taskHistories = useMemo(() => {
    return selectableTaskList.map((task) => {
      return {
        jobsetId: task.jobSetId,
        jobsetName: task.jobSetName,
        inputDate: task.inputDate,
      };
    });
  }, [selectableTaskList]);

  // Side effect for phase changed
  useLayoutEffect(() => {
    // Select panel
    switch (getCurrentPhase(taskControlState)) {
      case PHASE_SELECT_TASK:
        setCurrentPanel({
          panel: (
            <SelectTaskPanel
              onNewClick={goNew}
              taskList={taskHistories}
              onTaskSelected={handleTaskSelected}
            />
          ),
          backButton: {
            text: t("AWVUI-MSG29"),
            action: goBack,
          },
          forwardButton: {
            text: t("AWVUI-MSG51"),
            action: goNext,
          },
          InterruptTaskControlButton: {
            action: onInterrupt,
            text: "",
          },
        });
        break;
      case PHASE_SELECT_POINT:
        setCurrentPanel({
          panel: (
            <SelectPointPanel
              state={taskControlState}
              pointSourceList={pointSourceList}
              selectedMapNode={selectedNode}
              onPassPointsChanged={handlePassPointsChanged}
            />
          ),
          backButton: {
            text:
              taskControlState.process[0] === PHASE_SELECT_POINT
                ? t("AWVUI-MSG29")
                : t("AWVUI-MSG52"),
            action: goBack,
          },
          forwardButton: {
            text: t("AWVUI-MSG51"),
            action: goNext,
          },
          InterruptTaskControlButton: {
            action: onInterrupt,
            text: "",
          },
        });
        break;
      case PHASE_TASK_SETTING:
        // Set default task name if register mode and transit from select point
        setTaskData((current) => {
          return target.mode === "register" &&
            taskControlState.process[taskControlState.phase - 1] ===
              PHASE_SELECT_POINT
            ? { ...current, jobSetName: defaultTaskName.current }
            : current;
        });
        setCurrentPanel({
          panel: (
            <SettingPanel
              state={taskControlState}
              mode={target.mode}
              onValidated={(validation) => {
                const validationValues = Object.values(validation);
                setCanForward(validationValues.every((v) => v));
              }}
              setHighlight={setHighlightNodeIds}
            />
          ),
          backButton: {
            text: t("AWVUI-MSG52"),
            action: goBack,
          },
          forwardButton: {
            text: t("AWVUI-MSG51"),
            action: processingConfirm,
          },
          InterruptTaskControlButton: {
            action: onInterrupt,
            text: "",
          },
        });
        break;
      case PHASE_TASK_CONFIRM:
        setCanForward(true);
        setCurrentPanel({
          panel: <ConfirmPanel mode={target.mode} />,
          backButton:
            taskControlState.process === APPROVE_ONLY_PROCESSES
              ? {
                  text: t("AWVUI-MSG12"),
                  action: processingGoHomeCancel,
                }
              : {
                  text: t("AWVUI-MSG52"),
                  action: processingSettingBack,
                },
          forwardButton: {
            text: t("AWVUI-MSG60"),
            action: onSubmit,
          },
          InterruptTaskControlButton: {
            action: processingSettingInterrupt,
            text: "",
          },
        });
        break;
      default:
        break;
    }
  }, [
    onInterrupt,
    goBack,
    goNew,
    goNext,
    handlePassPointsChanged,
    handleTaskSelected,
    onSubmit,
    pointSourceList,
    processingConfirm,
    processingGoHomeCancel,
    processingSettingBack,
    processingSettingInterrupt,
    selectableTaskList,
    selectedNode,
    setTaskData,
    t,
    target,
    taskControlState,
    taskHistories,
  ]);

  // Effect for subscribing task creation status
  useEffect(() => {
    if (subscriptionContext !== undefined) {
      if (subscribeTimeOutId === 0) {
        const newTimeOutId = window.setTimeout(async () => {
          await subscribeTaskCreationStatus();
        }, INTERVAL_COUNT) as unknown as number;
        setSubscribeTimeOutId(newTimeOutId);
      }
    } else {
      if (subscribeTimeOutId > 0) {
        // Stop updating
        setSubscribeTimeOutId((current) => {
          window.clearTimeout(current);
          return 0;
        });
      }
    }
  }, [subscriptionContext, subscribeTimeOutId, subscribeTaskCreationStatus]);

  return (
    <Dialog
      maxWidth={false}
      fullWidth
      key={isOpen ? "open" : "closed"}
      open={isOpen}
      PaperProps={{ sx: { height: "100%" } }}
    >
      <DialogContainer sidePanelWidth="450px">
        <MapArea>
          <Map {...mapProps}>
            {isTilesLoaded && (
              <>
                <ResetMapPosition
                  center={mapProps.center}
                  zoom={mapProps.zoom}
                />
                <ResetMapHeading />
              </>
            )}
            <NodeMapOverlay
              mapData={mapDataRef.current}
              onNodeSelect={setSelectedNode}
            />
            {getCurrentPhase(taskControlState) === PHASE_TASK_CONFIRM ? (
              <HighlightEdgePolygon
                mapData={mapDataRef.current}
                highlightEdges={taskData.taskActualRouteInfo ?? []}
              />
            ) : (
              <HighlightNodePolygon
                mapData={mapDataRef.current}
                nodeIds={highlightNodeIds}
              />
            )}
            <AutoStartNodeMapOverlay nodes={autoStartNodes} />
          </Map>
        </MapArea>
        <DetailArea>
          {currentPanel && (
            <TaskControlPanel
              state={taskControlState}
              frameTypeAndNumber={taskData.frameTypeAndNumber}
              canForward={canForward}
              forwardButtonContext={currentPanel?.forwardButton}
              backButtonContext={currentPanel?.backButton}
              interruptButtonContext={currentPanel?.InterruptTaskControlButton}
            >
              {currentPanel?.panel}
            </TaskControlPanel>
          )}
        </DetailArea>
      </DialogContainer>
      <ConnectingDialog open={isConnecting} />
      <ErrorDialog
        open={isError}
        onPositiveButtonClick={() => {
          setIsError(false);

          // Close this
          finish(errorStatus.current);
        }}
        message={
          <Typography variant="inherit" sx={{ whiteSpace: "pre-wrap" }}>
            {errMsg}
          </Typography>
        }
        positiveButtonText={t("AWVUI-MSG22")}
      />
      <ConfirmDialog
        open={confirmContents !== undefined}
        title={confirmContents?.title}
        message={confirmContents?.message}
        positiveButtonText={confirmContents?.positiveButtonText ?? ""}
        onPositiveButtonClick={() => {
          confirmContents?.onPositiveButtonClick();
        }}
        negativeButtonText={confirmContents?.negativeButtonText}
        onNegativeButtonClick={confirmContents?.onNegativeButtonClick}
      />
      {showApiError && (
        <SystemErrorDialog {...showApiError} open={!!showApiError} />
      )}
      {(getTaskList.loading || getTask.loading) && <SpinnerDelay />}
    </Dialog>
  );
};

interface SubscriptionContext {
  jobsetId: string;
  timeout: {
    startTimestamp: Date;
    timeout: number;
  };
  onSucceed?: (jobsetId: string) => void;
  onError?: (error: unknown) => void;
}

interface SidePanelContext {
  panel: ReactNode;
  backButton: ButtonContext;
  forwardButton: ButtonContext;
  InterruptTaskControlButton: ButtonContext;
}

/**
 * Get list of point nodes
 * @param{MapData} mapData Map
 * @return{PointSource[]} List of point nodes
 */
function getPointSources(mapData: MapData): Array<PointSource> {
  const allNodes = mapData.mapInfo.flatMap((m) => m.nodes);
  const pointNodes = allNodes.filter(
    (n) => getNodeType(n?.description).type === "NamedNode"
  );
  return pointNodes.map((n, i) => {
    return {
      key: i + 1,
      nodeId: n?.id,
      label: getNodeType(n?.description).label,
    };
  });
}

const getErrMsgCode = (error: unknown, mode: TaskControlMode) => {
  if (error instanceof TimeoutError) {
    switch (mode) {
      case "register":
        return "AWVUI-MSG103";
      case "edit":
        return "AWVUI-MSG104";
      case "approve":
        return "AWVUI-MSG103";
      default:
        return "AWVUI-MSG86";
    }
  } else {
    switch (mode) {
      case "register":
        return "AWVUI-MSG47";
      case "edit":
        return "AWVUI-MSG102";
      case "approve":
        return "AWVUI-MSG47";
      default:
        return "AWVUI-MSG26";
    }
  }
};
